import React from 'react';
import { MapPin, Phone, Star, Clock, ShieldCheck, Plane } from 'lucide-react';

const PetShop = () => {
  const shops = [
    {
      name: 'Coimbatore Pet Boarding & Care',
      area: 'RS Puram',
      address: '14, Cross Cut Road, RS Puram, Coimbatore – 641002',
      rating: '4.9', reviews: 128, distance: '1.2 km',
      status: 'Open Now', statusColor: 'bg-green-100 text-green-700',
      hours: 'Mon–Sat: 9 AM – 9 PM',
      phone: '+91 98421 34567',
      services: ['Pet Boarding', 'Daycare', 'Grooming', 'Dog Walking'],
      accentColor: 'from-teal-400 to-cyan-500',
      highlight: 'Accepts same-day boarding',
    },
    {
      name: 'Paws & Whiskers Boarding Home',
      area: 'Gandhipuram',
      address: '7-A, Nehru Street, Gandhipuram, Coimbatore – 641009',
      rating: '4.8', reviews: 94, distance: '2.4 km',
      status: 'Open Now', statusColor: 'bg-green-100 text-green-700',
      hours: 'Daily: 8 AM – 10 PM',
      phone: '+91 94435 67812',
      services: ['Overnight Boarding', 'Pet Sitting', 'Bird Care'],
      accentColor: 'from-orange-400 to-amber-500',
      highlight: 'CCTV-monitored facility',
    },
    {
      name: 'Happy Tails Pet Daycare',
      area: 'Saibaba Colony',
      address: '22, Mettupalayam Road, Saibaba Colony, Coimbatore – 641011',
      rating: '4.7', reviews: 76, distance: '3.1 km',
      status: 'Closes at 9 PM', statusColor: 'bg-yellow-100 text-yellow-700',
      hours: 'Mon–Sat: 10 AM – 9 PM',
      phone: '+91 96001 22334',
      services: ['Dog Daycare', 'Cat Boarding', 'Bathing & Grooming'],
      accentColor: 'from-purple-400 to-pink-500',
      highlight: 'Individual rooms per pet',
    },
    {
      name: 'Fur & Feathers Pet Stay',
      area: 'Peelamedu',
      address: '5, Avinashi Road, Peelamedu, Coimbatore – 641004',
      rating: '4.8', reviews: 111, distance: '4.7 km',
      status: 'Open Now', statusColor: 'bg-green-100 text-green-700',
      hours: 'Daily: 9 AM – 8 PM',
      phone: '+91 93845 10021',
      services: ['Long-term Boarding', 'Medical Diet Pets', 'Pick & Drop'],
      accentColor: 'from-blue-400 to-indigo-500',
      highlight: 'Accepts medically-special pets',
    },
    {
      name: 'Nature\'s Nest Pet Care',
      area: 'Singanallur',
      address: '3, Trichy Road, Singanallur, Coimbatore – 641005',
      rating: '4.6', reviews: 58, distance: '5.9 km',
      status: 'Open Now', statusColor: 'bg-green-100 text-green-700',
      hours: 'Mon–Sun: 9 AM – 9 PM',
      phone: '+91 98430 77654',
      services: ['Exotic Pet Care', 'Reptile Boarding', 'Bird Sitting'],
      accentColor: 'from-green-400 to-emerald-500',
      highlight: 'Exotic & rare pet specialists',
    },
    {
      name: 'Petsville Boarding & Grooming',
      area: 'Race Course',
      address: '11, Race Course Road, Coimbatore – 641018',
      rating: '4.9', reviews: 143, distance: '2.8 km',
      status: 'Open Now', statusColor: 'bg-green-100 text-green-700',
      hours: 'Mon–Sat: 8 AM – 10 PM',
      phone: '+91 90038 19900',
      services: ['Premium Boarding', 'Grooming Spa', 'Vaccination Drive'],
      accentColor: 'from-rose-400 to-red-500',
      highlight: 'Premium air-conditioned suites',
    },
  ];

  return (
    <section id="petshop" className="py-24 bg-[#F7FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-block text-brand-orange font-bold tracking-widest uppercase text-xs mb-3 bg-orange-50 px-4 py-1.5 rounded-full border border-orange-100">
            For When You Travel
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Trusted Pet Boarding &<br/>Daycare in Coimbatore
          </h2>
          <p className="text-xl text-gray-500 leading-relaxed">
            Going on a trip? These verified boarding centres will care for your pet exactly as you would. 
            All contactable directly — no platform booking, no commissions.
          </p>
        </div>

        {/* Context banner */}
        <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5 flex items-start gap-4 mb-10">
          <Plane className="text-brand-orange flex-shrink-0 mt-0.5" size={22} />
          <div>
            <p className="font-bold text-gray-900 mb-1">How This Section Works</p>
            <p className="text-gray-600 text-sm leading-relaxed">
              These are Coimbatore-based pet shops and care centres verified to accept boarding and daycare requests. 
              Browse the services, check operating hours, and call them directly to confirm availability before your trip.
            </p>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {shops.map((shop, i) => (
            <div key={i} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col overflow-hidden">
              <div className={`h-1.5 bg-gradient-to-r ${shop.accentColor}`} />
              <div className="p-6 flex flex-col flex-grow">
                {/* Header row */}
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">{shop.name}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin size={12} className="text-brand-teal" />
                      <span className="text-xs text-gray-500">{shop.area}, Coimbatore</span>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${shop.statusColor} whitespace-nowrap ml-2 flex-shrink-0`}>{shop.status}</span>
                </div>

                {/* Highlight badge */}
                <div className="bg-gray-50 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 mb-3 inline-block w-fit border border-gray-100">
                  ✓ {shop.highlight}
                </div>

                <p className="text-xs text-gray-400 mb-3">{shop.address}</p>

                {/* Rating & hours */}
                <div className="flex items-center gap-3 mb-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Star size={12} className="text-yellow-400" fill="currentColor" />
                    <span className="font-semibold text-gray-800">{shop.rating}</span>
                    <span className="text-gray-400">({shop.reviews})</span>
                  </div>
                  <div className="w-px h-3 bg-gray-200" />
                  <Clock size={12} />
                  <span>{shop.hours}</span>
                </div>

                {/* Services */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {shop.services.map((s, t) => (
                    <span key={t} className="bg-gray-100 text-gray-600 text-xs px-2.5 py-1 rounded-full font-medium">{s}</span>
                  ))}
                </div>

                <div className="mt-auto">
                  <a href={`tel:${shop.phone}`} className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-brand-teal/10 text-brand-teal font-semibold hover:bg-brand-teal hover:text-white transition-colors border border-teal-100 text-sm">
                    <Phone size={15} /> {shop.phone}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
          <ShieldCheck size={15} className="text-brand-teal" />
          All boarding centres personally verified · Expanding to more South Indian cities soon
        </div>
      </div>
    </section>
  );
};

export default PetShop;
